import {
  Component, EventEmitter, HostListener, Input, OnChanges, OnInit,
  Output, SimpleChanges
} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {
  DEAL_PAYROLL_STATUS, DealPayrollBreakdown, DealPayrollTaskLine,
  isDealPayrollIdentityMatch
} from '../../../models/deal-payroll.model';
import {CurrencyAmount} from '../../../models/payroll-currency.model';
import {
  CHECKLIST_UNIT_TYPE, DETAILS_OPEN_ATTRIBUTE, DETAIL_ESCAPE_KEY,
  DETAIL_LEGACY_ESCAPE_KEY, PLANFIX_TASK_URL_PREFIX
} from './deal-payroll-detail.view.constants';
import {DealPayrollDetailFormulaState} from './deal-payroll-detail.formula-state';
import {DealPayrollDetailLoader} from './deal-payroll-detail.loader';
import {DealPayrollDetailLoaderContext} from './deal-payroll-detail.loader.types';
import {
  DealPayrollDetailTaskGroup, DealPayrollDetailTaskView
} from './deal-payroll-detail.view.types';
import {isDealPayrollTaskProblem} from './deal-payroll-detail.view';
import {DealPayrollDetailViewState} from './deal-payroll-detail.view-state';

@Component({
  selector: 'app-deal-payroll-detail',
  templateUrl: './deal-payroll-detail.component.html',
  styleUrls: [
    './deal-payroll-detail.component.css',
    './deal-payroll-detail.tasks.css',
    './deal-payroll-detail.task-rows.css',
    './deal-payroll-detail.formula.css',
    './deal-payroll-detail.responsive.css'
  ],
  providers: [
    DealPayrollDetailViewState,
    DealPayrollDetailFormulaState,
    DealPayrollDetailLoader
  ]
})
export class DealPayrollDetailComponent implements OnInit, OnChanges {
  @Input() userId: string;
  @Input() month: string;
  @Input() initialBreakdown: Object;
  @Input() calculation: Array<string | number | null> = [];
  @Input() calculationTrusted = true;
  @Output() breakdownLoaded = new EventEmitter<Object>();

  readonly statuses = DEAL_PAYROLL_STATUS;
  readonly planfixTaskUrlPrefix = PLANFIX_TASK_URL_PREFIX;
  readonly checklistUnitType = CHECKLIST_UNIT_TYPE;
  readonly viewState: DealPayrollDetailViewState;

  private initialized = false;

  constructor(
    public activeModal: NgbActiveModal,
    private formulaState: DealPayrollDetailFormulaState,
    private detailLoader: DealPayrollDetailLoader
  ) {
    this.viewState = detailLoader.viewState;
  }

  ngOnInit(): void {
    this.initialized = true;
    this.loadInitialBreakdown();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && this.hasDetailContextChanged(changes)) {
      this.loadInitialBreakdown();
    }
  }

  refresh(): void {
    this.formulaState.closeWithoutFocus();
    this.loadBreakdown(true);
  }

  dismiss(): void {
    if (this.canDismiss()) {
      this.activeModal.dismiss();
    }
  }

  close(): void {
    if (this.canDismiss()) {
      this.activeModal.close();
    }
  }

  loadBreakdown(refresh: boolean): void {
    this.detailLoader.load(this.loaderContext(refresh));
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByCurrency(_index: number, amount: CurrencyAmount): string {
    return amount.currency;
  }

  trackByDealPayrollTaskGroup(_index: number, group: DealPayrollDetailTaskGroup): string {
    return group.key;
  }

  trackByDealPayrollTask(_index: number, task: DealPayrollTaskLine): number {
    return task.taskId;
  }

  isDealPayrollTaskProblem(task: DealPayrollDetailTaskView): boolean {
    return isDealPayrollTaskProblem(task);
  }

  dealPayrollFormulaId(task: DealPayrollTaskLine): string {
    return this.formulaState.formulaId(task);
  }

  isDealPayrollFormulaOpen(task: DealPayrollTaskLine): boolean {
    return this.formulaState.isOpen(task);
  }

  toggleDealPayrollFormula(task: DealPayrollTaskLine, event: Event): void {
    this.formulaState.toggle(task, event);
  }

  toggleDealPayrollGroup(event: Event, group: DealPayrollDetailTaskGroup): void {
    const element = event.currentTarget as HTMLElement;
    if (element.hasAttribute(DETAILS_OPEN_ATTRIBUTE)) {
      this.viewState.openGroupKey = group.key;
      return;
    }
    if (this.viewState.openGroupKey === group.key) {
      this.viewState.openGroupKey = null;
    }
    this.formulaState.closeWithoutFocus();
  }

  @HostListener('document:click')
  handleDocumentClick(): void {
    this.formulaState.closeWithoutFocus();
  }

  @HostListener('document:keyup', ['$event'])
  handleDocumentKeyup(event: KeyboardEvent): void {
    const escape = event.key === DETAIL_ESCAPE_KEY || event.key === DETAIL_LEGACY_ESCAPE_KEY;
    if (!escape) {
      return;
    }
    event.preventDefault();
    if (this.formulaState.closeIfOpen()) {
      return;
    }
    this.dismiss();
  }

  private loadInitialBreakdown(): void {
    this.detailLoader.loadInitial(this.loaderContext(false));
  }

  private loaderContext(refresh: boolean): DealPayrollDetailLoaderContext {
    const initial = this.initialBreakdown as DealPayrollBreakdown;
    return {
      userId: this.userId,
      month: this.month,
      refresh: refresh,
      reference: isDealPayrollIdentityMatch(this.userId, this.month, initial) ? initial : undefined,
      getCalculation: () => this.calculation,
      isCalculationTrusted: () => this.calculationTrusted,
      emitBreakdown: (value: DealPayrollBreakdown) => this.breakdownLoaded.emit(value)
    };
  }

  private hasDetailContextChanged(changes: SimpleChanges): boolean {
    const userChange = changes.userId;
    const monthChange = changes.month;
    return !!userChange && userChange.previousValue !== userChange.currentValue
      || !!monthChange && monthChange.previousValue !== monthChange.currentValue;
  }

  private canDismiss(): boolean {
    return !this.formulaState.closeIfOpen();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/deal-payroll-detail/deal-payroll-detail.component.ts